import { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import "./App.css";
import { AuthProvider, ROLE_HOME, Role, useAuth } from "./auth/AuthProvider";
import { RealtimeProvider } from "./realtime/RealtimeProvider";
import { AppShell } from "./components/AppShell";
import { PwaBanners } from "./components/PwaBanners";
import { LoginPage } from "./pages/AuthPages";
import RegisterWizard from "./pages/RegisterWizard";
import ForgotPassword from "./pages/ForgotPassword";
import AdminPackages from "./pages/admin/AdminPackages";
import AdminCards from "./pages/admin/AdminCards";
import CashierActivation from "./pages/cashier/CashierActivation";
import Profile from "./pages/Profile";
import Notifications from "./pages/Notifications";
import Menu from "./pages/Menu";
import PatientHome from "./pages/patient/PatientHome";
import Book from "./pages/patient/Book";
import ScheduleTrip from "./pages/patient/ScheduleTrip";
import Bookings from "./pages/patient/Bookings";
import BookingDetail from "./pages/patient/BookingDetail";
import Membership from "./pages/patient/Membership";
import VerifyAccount from "./pages/patient/VerifyAccount";
import ResponderHome from "./pages/responder/ResponderHome";
import ResponderHistory from "./pages/responder/ResponderHistory";
import DispatchBoard from "./pages/dispatcher/DispatchBoard";
import DispatchHistory from "./pages/dispatcher/DispatchHistory";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminVerification from "./pages/admin/AdminVerification";
import AdminFleet from "./pages/admin/AdminFleet";
import AdminMemberships from "./pages/admin/AdminMemberships";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminConfig from "./pages/admin/AdminConfig";
import AdminAudit from "./pages/admin/AdminAudit";
import PaySimulated from "./pages/pay/PaySimulated";
import PayReturn from "./pages/pay/PayReturn";

function Protected({ roles, children }: { roles?: Role[]; children: ReactNode }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-8 text-slate-500" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={ROLE_HOME[user.role]} replace />;
  return <>{children}</>;
}

const shell = (roles?: Role[]) => <Protected roles={roles}><AppShell /></Protected>;

export default function App() {
  return (
    <div className="fp-app">
      <BrowserRouter>
        <AuthProvider>
          <RealtimeProvider>
            <PwaBanners />
            <Routes>
              <Route path="/" element={<LoginPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterWizard />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route element={shell(["cashier", "admin"])}>
                <Route path="/cashier" element={<CashierActivation />} />
              </Route>
              <Route element={shell()}>
                <Route path="/profile" element={<Profile />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/menu" element={<Menu />} />
                <Route path="/pay/simulated/:id" element={<PaySimulated />} />
                <Route path="/pay/return/:id" element={<PayReturn />} />
              </Route>
              <Route element={shell(["patient"])}>
                <Route path="/patient" element={<PatientHome />} />
                <Route path="/patient/emergency" element={<Book initial="sos" />} />
                <Route path="/patient/book" element={<Book initial="ptv" />} />
                <Route path="/patient/schedule" element={<ScheduleTrip />} />
                <Route path="/patient/bookings" element={<Bookings />} />
                <Route path="/patient/bookings/:id" element={<BookingDetail />} />
                <Route path="/patient/membership" element={<Membership />} />
                <Route path="/patient/verify" element={<VerifyAccount />} />
              </Route>
              <Route element={shell(["responder"])}>
                <Route path="/responder" element={<ResponderHome />} />
                <Route path="/responder/history" element={<ResponderHistory />} />
              </Route>
              <Route element={shell(["dispatcher", "admin"])}>
                <Route path="/dispatch" element={<DispatchBoard />} />
                <Route path="/dispatch/history" element={<DispatchHistory />} />
              </Route>
              <Route element={shell(["admin"])}>
                <Route path="/admin" element={<AdminAnalytics />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/verification" element={<AdminVerification />} />
                <Route path="/admin/fleet" element={<AdminFleet />} />
                <Route path="/admin/memberships" element={<AdminMemberships />} />
                <Route path="/admin/packages" element={<AdminPackages />} />
                <Route path="/admin/cards" element={<AdminCards />} />
                <Route path="/admin/transactions" element={<AdminTransactions />} />
                <Route path="/admin/config" element={<AdminConfig />} />
                <Route path="/admin/audit" element={<AdminAudit />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-center" richColors />
          </RealtimeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
